import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { fetchData, getAgentById } from "../../api/client";
import { useChatbotSlots, useChatbotActions, useChatbotMutations } from "../../hooks/useChatbot";
import {
  applyOptimizationSuggestion,
  describeSuggestionChange,
  enrichSuggestions,
  SUGGESTION_STATUS_APPLIED,
  SUGGESTION_STATUS_DISCARDED,
  SUGGESTION_STATUS_PENDING,
} from "../../utils/chatbotFlowOptimization";
import {
  buildFullFlowDocument,
  downloadFlowJson,
  slotsToFlowDocument,
} from "../../utils/chatbotFlowExchange";
import { ensureAlterarSolicitacaoAction } from "../../utils/chatbotAlterarSolicitacaoAction";
import { fetchAutomationRuleForExport } from "./ChatbotFlowImportWizard";
import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";
import ChatbotFlowPathLinks from "./ChatbotFlowPathLinks";
import ChatbotFlowDialogSlotEditor, {
  ChatbotFlowDialogSplitLayout,
} from "./ChatbotFlowDialogSlotEditor";

const STEPS = { intro: 0, analyzing: 1, review: 2 };

function priorityColor(priority) {
  if (priority === "high") return "error";
  if (priority === "low") return "default";
  return "warning";
}

function SuggestionCard({
  suggestion,
  slots,
  t,
  busyId,
  onApply,
  onDiscard,
  onNavigateToSlot,
  expanded,
  onToggleExpand,
}) {
  const changes = describeSuggestionChange(suggestion, slots);
  const isPending = suggestion.status === SUGGESTION_STATUS_PENDING;
  const isApplied = suggestion.status === SUGGESTION_STATUS_APPLIED;
  const isDiscarded = suggestion.status === SUGGESTION_STATUS_DISCARDED;
  const isBusy = busyId === suggestion.id;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: isApplied ? "success.light" : isDiscarded ? "divider" : "grey.300",
        borderRadius: 1,
        p: 1.5,
        opacity: isDiscarded ? 0.65 : 1,
        bgcolor: isApplied ? "success.50" : "background.paper",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 0.5 }}>
            <Chip
              size="small"
              label={t(`chatbotFlowOptimization.type_${suggestion.type}`, { defaultValue: suggestion.type })}
              variant="outlined"
            />
            <Chip
              size="small"
              color={priorityColor(suggestion.priority)}
              label={t(`chatbotFlowOptimization.priority_${suggestion.priority}`, {
                defaultValue: suggestion.priority,
              })}
              variant="outlined"
            />
            {isApplied && (
              <Chip size="small" color="success" icon={<CheckCircleOutline />} label={t("chatbotFlowOptimization.appliedBadge")} />
            )}
            {isDiscarded && (
              <Chip size="small" label={t("chatbotFlowOptimization.discardedBadge")} variant="outlined" />
            )}
          </Stack>
          <Typography variant="subtitle2" fontWeight={700}>
            {suggestion.title}
          </Typography>
          <Box sx={{ mt: 0.25 }}>
            <ChatbotFlowPathLinks
              path={suggestion.slotPath}
              slots={slots}
              targetSlotId={suggestion.slotId}
              onNavigate={onNavigateToSlot}
              variant="caption"
            />
          </Box>
          {suggestion.reason && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {suggestion.reason}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onToggleExpand} aria-label={t("chatbotFlowOptimization.toggleDetails")}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Stack>

      <Collapse in={expanded}>
        <Stack spacing={0.75} sx={{ mt: 1.25 }}>
          {changes.map((line) => (
            <Box key={`${suggestion.id}-${line.field}`}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t(`chatbotFlowOptimization.field_${line.field}`, { defaultValue: line.field })}
              </Typography>
              <Typography variant="body2" sx={{ textDecoration: isDiscarded ? "line-through" : "none" }}>
                {t("chatbotFlowOptimization.changePreview", {
                  before: line.before || t("chatbotFlowOptimization.emptyValue"),
                  after: line.after || t("chatbotFlowOptimization.emptyValue"),
                })}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>

      {isPending && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            disabled={isBusy}
            onClick={() => onApply(suggestion)}
            sx={{ textTransform: "none" }}
          >
            {isBusy ? <CircularProgress size={16} /> : t("chatbotFlowOptimization.applyOne")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={isBusy}
            onClick={() => onDiscard(suggestion.id)}
            sx={{ textTransform: "none" }}
          >
            {t("chatbotFlowOptimization.discardOne")}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default function ChatbotFlowOptimizationDialog({
  userId,
  chatbotName,
  disabled,
  onError,
  onSuccess,
  onNavigateToSlot,
}) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.intro);
  const [suggestions, setSuggestions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [exportBusy, setExportBusy] = useState(false);
  const [analyzeBusy, setAnalyzeBusy] = useState(false);
  const [applyBusyId, setApplyBusyId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [nextOffset, setNextOffset] = useState(0);
  const [previewSlotId, setPreviewSlotId] = useState(null);

  const BATCH_SIZE = 50;

  const { data: slots = [], isLoading, refetch } = useChatbotSlots(userId);
  const { data: actionsCatalog = [] } = useChatbotActions(userId);
  const { updateSlot } = useChatbotMutations(userId);

  const isLargeFlow = slots.length > BATCH_SIZE;

  const alterarSolicitacaoForm = useMemo(() => {
    const catalog = ensureAlterarSolicitacaoAction(actionsCatalog);
    const action = catalog.find((a) => Number(a.tipo_acao) === 4 && Number(a.id_acao) === 4);
    return action?.parameterForm || null;
  }, [actionsCatalog]);

  const pendingSuggestions = useMemo(
    () => suggestions.filter((s) => s.status === SUGGESTION_STATUS_PENDING),
    [suggestions]
  );

  const safeFileName = (chatbotName || "chatbot-fluxo").replace(/[^-\w\s]+/g, "").trim() || "chatbot-fluxo";

  const buildExportLoaders = () => ({
    fetchAutomationRule: fetchAutomationRuleForExport,
    fetchUser: async (id) => {
      const res = await fetchData(`/users/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchAgent: async (id) => getAgentById(id),
    fetchChannel: async (id) => {
      const res = await fetchData(`/channels/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchType: async (id) => {
      const res = await fetchData(`/types/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchSubtype: async (id) => {
      const res = await fetchData(`/types/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchPaymentMethod: async (id) => {
      const res = await fetchData(`/payment-methods/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchDeliveryMethod: async (id) => {
      const res = await fetchData(`/delivery-methods/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchPosition: async (id) => {
      const res = await fetchData(`/positions/${id}`, "GET");
      return res?.data ?? res;
    },
  });

  const handleOpen = () => {
    setStep(STEPS.intro);
    setSuggestions([]);
    setMeta(null);
    setExpanded(new Set());
    setDialogError(null);
    setNextOffset(0);
    setPreviewSlotId(null);
    setOpen(true);
  };

  const handleClose = () => {
    if (analyzeBusy || batchBusy || applyBusyId) return;
    setPreviewSlotId(null);
    setOpen(false);
  };

  const handleNavigateToSlot = (slotId) => {
    if (!slotId) return;
    setPreviewSlotId(Number(slotId));
    onNavigateToSlot?.(Number(slotId));
  };

  const handleExportBackup = async () => {
    if (!slots.length) {
      onError?.(t("chatbotConfigPage.flowExportEmpty"));
      return;
    }
    setExportBusy(true);
    try {
      const doc = await buildFullFlowDocument(slots, {
        chatbotName,
        sourceUserId: userId,
        parameterForm: alterarSolicitacaoForm,
        loaders: buildExportLoaders(),
      });
      downloadFlowJson(doc, `${safeFileName}-backup-optimization`);
      onSuccess?.(t("chatbotFlowOptimization.exportSuccess"));
    } catch {
      downloadFlowJson(
        slotsToFlowDocument(slots, { chatbotName, sourceUserId: userId }),
        `${safeFileName}-backup-optimization`
      );
      onSuccess?.(t("chatbotFlowOptimization.exportSuccess"));
    } finally {
      setExportBusy(false);
    }
  };

  const mergeSuggestions = (prev, incoming) => {
    const seen = new Set(prev.map((s) => s.id));
    return [...prev, ...incoming.filter((s) => !seen.has(s.id))];
  };

  const runAnalyzeBatch = async ({ continueBatch = false } = {}) => {
    if (!slots.length) {
      setDialogError(t("chatbotConfigPage.flowExportEmpty"));
      return;
    }
    setDialogError(null);
    setAnalyzeBusy(true);
    const offset = continueBatch ? nextOffset : 0;
    if (!continueBatch) {
      setStep(STEPS.analyzing);
      setSuggestions([]);
      setNextOffset(0);
      setMeta(null);
    }
    try {
      const res = await fetchData("/chatbot/slots/suggest-optimizations", "POST", {
        user_id: userId,
        offset,
        batch_size: BATCH_SIZE,
      });
      const data = res?.data ?? res ?? {};
      const incoming = enrichSuggestions(data.suggestions || [], slots);
      setSuggestions((prev) => (continueBatch ? mergeSuggestions(prev, incoming) : incoming));
      setMeta(data.meta || null);
      setNextOffset(data.meta?.nextOffset ?? offset + (data.meta?.batchSize || BATCH_SIZE));
      setExpanded((prev) => {
        const next = continueBatch ? new Set(prev) : new Set();
        incoming.slice(0, 3).forEach((s) => next.add(s.id));
        return next;
      });
      setStep(STEPS.review);
      if (!incoming.length && !continueBatch) {
        onSuccess?.(t("chatbotFlowOptimization.noSuggestions"));
      } else if (continueBatch && incoming.length) {
        onSuccess?.(
          t("chatbotFlowOptimization.batchComplete", {
            count: incoming.length,
            analyzed: data.meta?.analyzedCount ?? 0,
            total: data.meta?.totalSlots ?? slots.length,
          })
        );
      }
    } catch (err) {
      if (!continueBatch) setStep(STEPS.intro);
      setDialogError(getUserFacingApiMessage(err).message || t("chatbotFlowOptimization.analyzeError"));
    } finally {
      setAnalyzeBusy(false);
    }
  };

  const handleAnalyze = () => runAnalyzeBatch({ continueBatch: false });

  const handleAnalyzeNext = () => runAnalyzeBatch({ continueBatch: true });

  const markSuggestion = (id, status) => {
    setSuggestions((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
  };

  const handleApplyOne = async (suggestion) => {
    setApplyBusyId(suggestion.id);
    setDialogError(null);
    try {
      await applyOptimizationSuggestion(suggestion, { updateSlot, slots });
      await refetch();
      await queryClient.invalidateQueries(["chatbot"]);
      markSuggestion(suggestion.id, SUGGESTION_STATUS_APPLIED);
      onSuccess?.(t("chatbotFlowOptimization.applyOneSuccess"));
    } catch (err) {
      setDialogError(err?.message || t("chatbotFlowOptimization.applyError"));
    } finally {
      setApplyBusyId(null);
    }
  };

  const handleApplyAll = async () => {
    if (!pendingSuggestions.length) return;
    setBatchBusy(true);
    let applied = 0;
    let failed = 0;
    try {
      for (const suggestion of pendingSuggestions) {
        try {
          await applyOptimizationSuggestion(suggestion, { updateSlot, slots });
          markSuggestion(suggestion.id, SUGGESTION_STATUS_APPLIED);
          applied += 1;
        } catch {
          failed += 1;
        }
      }
      await refetch();
      await queryClient.invalidateQueries(["chatbot"]);
      onSuccess?.(t("chatbotFlowOptimization.applyAllSuccess", { applied, failed }));
    } finally {
      setBatchBusy(false);
    }
  };

  const handleDiscardAll = () => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.status === SUGGESTION_STATUS_PENDING ? { ...s, status: SUGGESTION_STATUS_DISCARDED } : s
      )
    );
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AutoFixHighOutlined />}
        disabled={disabled || !userId}
        onClick={handleOpen}
        sx={{ textTransform: "none" }}
      >
        {t("chatbotFlowOptimization.openButton")}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={previewSlotId && step === STEPS.review ? "xl" : "md"}
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t("chatbotFlowOptimization.title")}
          <IconButton
            aria-label={t("chatbotFlowOptimization.close")}
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={
            step === STEPS.review
              ? {
                  p: 0,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: previewSlotId ? 520 : undefined,
                }
              : undefined
          }
        >
          {dialogError && (
            <Alert severity="error" onClose={() => setDialogError(null)} sx={{ m: 2, mb: step === STEPS.review ? 0 : 2 }}>
              {dialogError}
            </Alert>
          )}

          {step === STEPS.intro && (
            <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2}>
              <Alert severity="info">{t("chatbotFlowOptimization.introHint")}</Alert>
              {isLargeFlow && (
                <Alert severity="info">
                  {t("chatbotFlowOptimization.batchIntroHint", {
                    size: BATCH_SIZE,
                    total: slots.length,
                  })}
                </Alert>
              )}
              <Typography variant="body2">{t("chatbotFlowOptimization.introBody")}</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={exportBusy ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
                onClick={handleExportBackup}
                disabled={exportBusy || isLoading || !slots.length}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                {t("chatbotFlowOptimization.exportBackupButton")}
              </Button>
            </Stack>
            </Box>
          )}

          {step === STEPS.analyzing && (
            <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {isLargeFlow
                  ? t("chatbotFlowOptimization.analyzingBatch", {
                      from: 1,
                      to: Math.min(BATCH_SIZE, slots.length),
                    })
                  : t("chatbotFlowOptimization.analyzing")}
              </Typography>
            </Stack>
            </Box>
          )}

          {step === STEPS.review && (
            <ChatbotFlowDialogSplitLayout
              slotEditorOpen={!!previewSlotId}
              slotEditor={
                <ChatbotFlowDialogSlotEditor
                  userId={userId}
                  slotId={previewSlotId}
                  slots={slots}
                  onClose={() => setPreviewSlotId(null)}
                  onError={(msg) => msg && setDialogError(msg)}
                />
              }
            >
            <Stack spacing={2} sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={t("chatbotFlowOptimization.summaryTotal", { count: suggestions.length })} size="small" />
                <Chip
                  label={t("chatbotFlowOptimization.summaryPending", { count: pendingSuggestions.length })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {meta?.batchMode && (
                  <Chip
                    label={t("chatbotFlowOptimization.progressAnalyzed", {
                      analyzed: meta.analyzedCount ?? meta.batchSize ?? 0,
                      total: meta.totalSlots ?? slots.length,
                    })}
                    size="small"
                    color={meta.hasMore ? "warning" : "success"}
                    variant="outlined"
                  />
                )}
              </Stack>

              {analyzeBusy && meta?.batchMode && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                      {t("chatbotFlowOptimization.analyzingBatch", {
                        from: nextOffset + 1,
                        to: Math.min(nextOffset + BATCH_SIZE, meta.totalSlots ?? slots.length),
                      })}
                    </Typography>
                  </Stack>
                </Alert>
              )}

              {suggestions.length === 0 && !analyzeBusy ? (
                <Alert severity="success">{t("chatbotFlowOptimization.noSuggestions")}</Alert>
              ) : (
                <Stack spacing={1.25}>
                  {suggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      slots={slots}
                      t={t}
                      busyId={applyBusyId}
                      expanded={expanded.has(suggestion.id)}
                      onToggleExpand={() => toggleExpanded(suggestion.id)}
                      onApply={handleApplyOne}
                      onDiscard={(id) => markSuggestion(id, SUGGESTION_STATUS_DISCARDED)}
                      onNavigateToSlot={handleNavigateToSlot}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
            </ChatbotFlowDialogSplitLayout>
          )}
        </DialogContent>

        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, py: 1.5 }}>
          {step === STEPS.intro && (
            <>
              <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                {t("chatbotFlowOptimization.close")}
              </Button>
              <Button
                variant="contained"
                onClick={handleAnalyze}
                disabled={isLoading || analyzeBusy || !slots.length}
                sx={{ textTransform: "none" }}
              >
                {t("chatbotFlowOptimization.startAnalyze")}
              </Button>
            </>
          )}

          {step === STEPS.review && (
            <>
              <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                {t("chatbotFlowOptimization.close")}
              </Button>
              {meta?.hasMore && (
                <Button
                  variant="outlined"
                  onClick={handleAnalyzeNext}
                  disabled={analyzeBusy || batchBusy || !!applyBusyId}
                  sx={{ textTransform: "none" }}
                >
                  {analyzeBusy ? (
                    <CircularProgress size={18} />
                  ) : (
                    t("chatbotFlowOptimization.analyzeNextBatch", {
                      count: Math.min(BATCH_SIZE, (meta.totalSlots ?? slots.length) - (meta.analyzedCount ?? 0)),
                    })
                  )}
                </Button>
              )}
              <Button
                variant="outlined"
                color="inherit"
                disabled={!pendingSuggestions.length || batchBusy || !!applyBusyId || analyzeBusy}
                onClick={handleDiscardAll}
                sx={{ textTransform: "none" }}
              >
                {t("chatbotFlowOptimization.discardAll")}
              </Button>
              <Button
                variant="contained"
                disabled={!pendingSuggestions.length || batchBusy || !!applyBusyId || analyzeBusy}
                onClick={handleApplyAll}
                sx={{ textTransform: "none" }}
              >
                {batchBusy ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  t("chatbotFlowOptimization.applyAll", { count: pendingSuggestions.length })
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
